import { useEffect, useRef, useState } from 'react';
import Navbar from './Navbar';
import NotificationToast from './NotificationToast';
import ShutdownOverlay from './ShutdownOverlay';
import Onboarding from './Onboarding';
import { useGameStore } from '../stores/gameStore';
import { useOnboardingStore } from '../stores/onboardingStore';
import { useTransferStore, justCompletedIds } from '../stores/transferStore';
import { useNotificationStore } from '../stores/notificationStore';
import { softwareApi, fileSystemApi } from '../services/api';
import { sfx } from '../services/sfx';
import { requestOpeningAd } from '../services/ads';
import { isAdBreakActive } from '../services/adBreak';
import { Player } from '../types';

interface LayoutProps {
  children: React.ReactNode;
}

const TICK_MS  = 100;
const TICK_SEC = TICK_MS / 1000;

// Encrypted-HD backups complete on a timer (readyAt), not through the transfer
// store, so they get no completion toast on their own. Track backups seen as
// in-progress and fire a one-time notification when each crosses readyAt — never
// for backups that were already finished when the page loaded.
const seenPendingBackups = new Set<string>();
const notifiedBackups    = new Set<string>();

function checkBackupCompletions(player: Player) {
  const hd = player.encryptedHd;
  if (!hd) return;
  const now  = Date.now();
  const push = useNotificationStore.getState().push;

  const items = [
    ...hd.software.map(s => ({
      key: `hd-sw-${s.type}-${s.readyAt}`,
      readyAt: s.readyAt,
      message: `${s.type} v${s.version.toFixed(1)} backed up to the encrypted HD.`,
    })),
    ...hd.files.map(f => ({
      key: `hd-file-${f.name}-${f.readyAt}`,
      readyAt: f.readyAt,
      message: `${f.name} backed up to the encrypted HD.`,
    })),
  ];

  for (const it of items) {
    if (new Date(it.readyAt).getTime() > now) { seenPendingBackups.add(it.key); continue; }
    if (seenPendingBackups.has(it.key) && !notifiedBackups.has(it.key)) {
      notifiedBackups.add(it.key);
      seenPendingBackups.delete(it.key);
      push({
        id: it.key,
        type: 'backup_complete',
        title: 'BACKUP COMPLETE',
        message: it.message,
        createdAt: new Date().toISOString(),
      });
    }
  }
}

export default function Layout({ children }: LayoutProps) {
  const loadPlayer = useGameStore(s => s.loadPlayer);
  const playerRef  = useRef(useGameStore.getState().player);
  const tickAllRef = useRef(useTransferStore.getState().tickAll);

  // Detect that a newer build has been deployed while this SPA stays loaded: poll the
  // freshly-served version.json and compare with the version baked into this bundle.
  const [outdated, setOutdated] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && typeof data?.version === 'string' && data.version !== __APP_VERSION__) {
          setOutdated(true);
        }
      } catch { /* offline, or version.json absent (dev) — ignore */ }
    };
    check();
    const id = setInterval(check, 60_000);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, []);

  useEffect(() => {
    const u1 = useGameStore.subscribe(s    => { playerRef.current  = s.player; });
    const u2 = useTransferStore.subscribe(s => { tickAllRef.current = s.tickAll; });
    return () => { u1(); u2(); };
  }, []);

  useEffect(() => {
    const id = setInterval(async () => {
      const player = playerRef.current;
      if (!player) return;
      // An ad is on screen: pause. Ticks are incremental (deltaSec), so skipping them simply
      // holds every transfer where it is until the ad ends — and no completion cue or toast
      // can fire over the ad, which is what Y8's QA checks for. See services/adBreak.ts.
      if (isAdBreakActive()) return;

      // Fire a toast when an encrypted-HD backup finishes uploading.
      checkBackupCompletions(player);

      // Snapshot transfers BEFORE tick so we can read names/versions of completed ones.
      const snapshot = useTransferStore.getState().transfers;
      const { completedSoftware, completedFiles } = tickAllRef.current(TICK_SEC, player.internetSpeedMBit);
      if (completedSoftware.length === 0 && completedFiles.length === 0) return;

      // One cue per batch (not per item) so finishing several at once isn't noisy.
      sfx.transfer();

      // Confirm software installs
      await Promise.allSettled(completedSoftware.map(sid => softwareApi.confirmInstall(sid)));

      // Save completed text-file downloads into player profile
      await Promise.allSettled(
        completedFiles.map(f => fileSystemApi.completeDownload(f.fileName, f.content, f.sizeMb, f.id))
      );

      // Push download-complete notifications
      const { push } = useNotificationStore.getState();
      const now = new Date().toISOString();

      completedSoftware.forEach(sid => {
        const t = snapshot.find(x => x.softwareId === sid);
        push({
          id: `sw-done-${sid}`,
          type: 'download_complete',
          title: 'DOWNLOAD COMPLETE',
          message: t
            ? `${t.name}${t.version != null ? ` v${t.version.toFixed(1)}` : ''} (${t.fileSizeMb.toFixed(1)} MB) installed.`
            : 'Software installed successfully.',
          createdAt: now,
        });
      });

      completedFiles.forEach(f => {
        push({
          id: `file-done-${f.id}`,
          type: 'download_complete',
          title: 'FILE DOWNLOAD COMPLETE',
          message: `${f.fileName} (${f.sizeMb.toFixed(2)} MB) saved to local drive.`,
          createdAt: now,
        });
      });

      await loadPlayer();

      // Release the completion guards now that the backend has confirmed and the
      // fresh player (isDownloaded=true) has been loaded. Leaving them set would
      // permanently block restoreDownloads from re-creating a transfer for a LATER
      // download/upgrade of the SAME id — the reported "start a new upgrade right
      // after finishing one → no progress bar and it never completes" bug (a reload
      // recreated the module and cleared the set, which is why it worked again).
      completedSoftware.forEach(sid => {
        justCompletedIds.delete(`sw-${sid}`);
        const tid = snapshot.find(x => x.softwareId === sid)?.id;
        if (tid) justCompletedIds.delete(tid);
      });
      completedFiles.forEach(f => justCompletedIds.delete(f.id));
    }, TICK_MS);

    return () => clearInterval(id);
  }, [loadPlayer]);

  // The opening ad (Y8's 'start' placement) — and NEVER to a player who has not played.
  //
  // An interstitial is a full-screen overlay, so serving one to somebody who has just
  // created an account is the fastest way to lose them; ROOTKIN's first impression is
  // already a text-dense console. The gate is the tutorial: `status === 'done'` means the
  // player finished it, skipped it, or was never new in the first place.
  //
  // BOTH halves of the condition are load-bearing. The onboarding record is persisted in
  // localStorage per player, so a returning visitor still sitting on the LOGIN screen can
  // already read 'done' — from the PREVIOUS account on that browser — which would serve an
  // ad before anyone had logged in at all. Requiring the record to belong to the player
  // who is actually signed in is what closes that.
  //
  // Selectors are deliberately narrow (an id and two scalars): Layout re-renders every
  // child, and subscribing to the whole player document would re-render the app on every
  // 15-second poll. Firing more than once is harmless anyway — the opening break is
  // once-per-page-load inside the wrapper.
  const playerId   = useGameStore(s => s.player?.id ?? null);
  const obPlayerId = useOnboardingStore(s => s.playerId);
  const obStatus   = useOnboardingStore(s => s.status);
  useEffect(() => {
    if (!playerId || obPlayerId !== playerId || obStatus !== 'done') return;
    void requestOpeningAd('session-start');
  }, [playerId, obPlayerId, obStatus]);

  // Light periodic refresh so a node shut down (or freed) by someone else is
  // picked up promptly even while the player is idle.
  useEffect(() => {
    const id = setInterval(() => {
      if (playerRef.current) loadPlayer();
    }, 15000);
    return () => clearInterval(id);
  }, [loadPlayer]);

  return (
    <div className="app">
      <Navbar />
      {outdated && (
        <div
          role="alert"
          style={{
            background: '#7c2d12',
            borderBottom: '1px solid #f97316',
            color: '#fed7aa',
            padding: '0.5rem 1rem',
            textAlign: 'center',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <span>⚠ You're running an outdated version of the game. Refresh to get the latest update.</span>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#f97316',
              color: '#000',
              border: 'none',
              borderRadius: '4px',
              padding: '0.25rem 0.8rem',
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontWeight: 'bold',
            }}
          >
            Refresh now
          </button>
        </div>
      )}
      {/* The only scrolling box in the app — see `.app-scroll` in App.css. The page and
          the footer live INSIDE it so the document itself never scrolls: every portal
          frames the app at a fixed stage, so a document scrollbar reads as the game
          failing to fit its own frame (Y8's review said exactly that). The navbar and
          the outdated-build banner stay outside it, pinned. */}
      <div className="app-scroll">
        <main className="main-content">
          {children}
        </main>
        {/* Version footer: in normal flow at the end of the page (not fixed), so it
            scrolls with the content and is only seen when you reach the bottom. */}
        <footer
          aria-hidden
          style={{
            textAlign: 'center',
            fontFamily: 'monospace',
            fontSize: '0.72rem',
            letterSpacing: '0.04em',
            color: 'var(--text-color)',
            opacity: 0.8,
            padding: '1rem 0 1.25rem',
            marginTop: '2rem',
            borderTop: '1px solid var(--border-color)',
          }}
        >
          {__APP_VERSION__}
        </footer>
      </div>
      <NotificationToast />
      <Onboarding />
      <ShutdownOverlay />
    </div>
  );
}
